import { dequal } from 'dequal';

import type { DropdownOption, DropdownTemplate } from '../../dropdown/types';
import { compose } from '@nf/utils-common/fpw/libs';
import { useToggle } from '@nf/utils-common/hooks';

export interface AccordionGroupOption {
	group: string;
	options: Array<DropdownOption>;
}

export interface AccordionDropdownEvent {
	groupOption: AccordionGroupOption;
	itemOption: DropdownOption;
}

export interface UseAccordionDropdownProps {
	groupItems: AccordionGroupOption[];
	valueGroup?: string;
	value?: any;
	valueTemplate?: DropdownTemplate;
	indicatorIcon?: React.ReactNode;
	onChange?(event: AccordionDropdownEvent): void;
}

export const useAccordionDropdown = (props: UseAccordionDropdownProps) => {
	const [isMenuOpen, toggleMenuOpen, setIsMenuOpen] = useToggle(false);
	const {
		groupItems = [],
		valueGroup,
		value,
		valueTemplate,
		onChange = () => {},
		...other
	} = props;

	const closeMenu = () => setIsMenuOpen(false);

	const getSelectedOption = () => {
		const selectedGroup = groupItems.find(groupItem => groupItem.group === valueGroup);
		const selectedItem = selectedGroup?.options?.find(option => dequal(option.value, value))
		return { selectedGroup, selectedItem };
	};

	const selectedOption = getSelectedOption();

	const createTriggerLabel = () => {
		const { selectedItem } = selectedOption;

		if (selectedItem && valueTemplate) {
			return valueTemplate(selectedItem);
		}

		return selectedItem?.label || '';
	};

	const getOptionProps = (
		groupOption: AccordionGroupOption, 
		itemOption: DropdownOption, 
		props = {}
	) => {
		return {
			'data-selected': groupOption.group === valueGroup && itemOption.value === value,
			onClick: () => compose(
				closeMenu,
				onChange,
			)({ groupOption, itemOption }),
			...props,
		};
	};

	return {
		groupItems,
		valueGroup,
		value,
		isMenuOpen,
		toggleMenuOpen,
		closeMenu,
		setIsMenuOpen,
		getSelectedOption,
		createTriggerLabel,
		getOptionProps,
		...other
	};
};